import { Banner, Form, Heading, ToggleRadio } from '@boltenergy-be/design-system'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { PostalCodeFormInput } from './types'
import { CustomerFlows, DeliveryAddressFieldKeys, InputGroupKeys, PersonalDataFieldKeys } from 'store/customer/enums'
import { useTranslation } from 'next-i18next/pages'
import { nextStep, updateInput } from 'store/customer/slice'
import { useStoreDispatch, useStoreSelector } from 'hooks/store'
import { getProductName, getUsableProducts } from 'utils/products'
import { useGetLanguage } from 'hooks/useGetLanguage'
import useSelectInputGroup from 'hooks/useSelectInputGroup'
import { setPostHogPersonProperties, trackEvent, trackGTMEvent } from 'utils/tracking'
import { PostalCodeAndTown } from 'components/inputs'
import SubmitButton from 'features/simulation/components/submit-button/SubmitButton'
import { useState } from 'react'
import { CUSTOMER_TYPES } from '@boltenergy-be/shared'
import { CommonTrackingParams } from 'types/tracking'
import { useGetTownsQuery } from 'store/api/bolt-api'
import { POSTAL_CODE } from 'constants/regex'
import { isValidPostalCode } from 'utils/helpers'

const PostalCodeStep = () => {
  // Redux store
  const simulateSpecificProduct = useStoreSelector((store) => store.customer.propositions.simulateSpecificProduct)
  const { isCompany } = useSelectInputGroup(InputGroupKeys.PERSONAL_DATA)
  const { address } = useSelectInputGroup(InputGroupKeys.DELIVERY_ADDRESS)
  const { region } = useSelectInputGroup(InputGroupKeys.SIMULATION)
  const dispatch = useStoreDispatch()

  // i18n
  const { t } = useTranslation(['simulation', 'common'])
  const language = useGetLanguage()

  // Local state
  const [fetchingTowns, setFetchingTowns] = useState<boolean>(false)

  // REACT HOOK FORM
  const hookForm = useForm<PostalCodeFormInput>({
    defaultValues: {
      userType: isCompany ? CUSTOMER_TYPES.professional : CUSTOMER_TYPES.residential,
      postalCode: address.postalCode,
      townCode: address.townCode,
      townName: address.townName
    },
    mode: 'onChange'
  })
  const {
    formState: { errors },
    handleSubmit,
    control,
    register
  } = hookForm
  const watchPostalCode = useWatch({ control, name: 'postalCode' })

  // Redux query
  // Deduped with the query in PostalCodeAndTown; currentData is only set for the current postal code, unlike the store region
  const { currentData: townsData } = useGetTownsQuery(watchPostalCode, { skip: !POSTAL_CODE.test(watchPostalCode ?? '') })

  // Constants
  const usableProducts = townsData?.region ? getUsableProducts({ region: townsData.region, withPlenty: true }) : null
  const selectedProductNotAvailableInCurrentRegion =
    usableProducts && simulateSpecificProduct && !usableProducts.includes(simulateSpecificProduct)

  /**
   * Handles the form submit after validation by React Hook Form
   */
  const onSubmit = (data: PostalCodeFormInput) => {
    const { userType, postalCode, townCode, townName } = data

    // Store region for the proposition-step A/B test without identifying the visitor.
    setPostHogPersonProperties({
      [CommonTrackingParams.REGION]: region
    })

    dispatch(
      updateInput({
        group: InputGroupKeys.PERSONAL_DATA,
        key: PersonalDataFieldKeys.IS_COMPANY,
        value: userType === CUSTOMER_TYPES.professional
      })
    )
    dispatch(
      updateInput({
        group: InputGroupKeys.DELIVERY_ADDRESS,
        key: DeliveryAddressFieldKeys.ADDRESS,
        value: {
          ...address,
          postalCode,
          townCode,
          townName
        }
      })
    )

    // Track 'Simulation step finished' event @Semetis
    trackGTMEvent('simulation_step_finished:postal-code', {
      postcode: postalCode
    })
    trackEvent({ category: 'simulation', object: 'postal_code_step', action: 'finish' })

    dispatch(nextStep({ flow: CustomerFlows.SIMULATION }))
  }

  return (
    <>
      <Heading as="h1" variant="h3" serif>
        {t('simulation:steps.postalCode.title')}
      </Heading>

      <FormProvider {...hookForm}>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <ToggleRadio
            label={t('simulation:fields.clientType.label')}
            grid="full"
            name="userType"
            error={errors?.userType?.message}
            options={[
              {
                ...register('userType', { required: t('validation:required') }),
                value: CUSTOMER_TYPES.residential,
                id: CUSTOMER_TYPES.residential,
                label: t('common:residential')
              },
              {
                ...register('userType', { required: t('validation:required') }),
                value: CUSTOMER_TYPES.professional,
                id: CUSTOMER_TYPES.professional,
                label: t('common:company')
              }
            ]}
          />

          <PostalCodeAndTown grid="full" hideTownSelectField {...{ setFetchingTowns }} />

          {isValidPostalCode(watchPostalCode) && selectedProductNotAvailableInCurrentRegion && (
            <Banner
              title={t('simulation:steps.postalCode.alerts.notAvailableInRegion', {
                products: usableProducts.map((product) => getProductName(product, language, true)).join(', ')
              })}
              type="warning"
              className="grid-col-full"
            />
          )}

          <Form.Footer>
            <SubmitButton loading={fetchingTowns} disabled={'postalCode' in errors || !watchPostalCode} />
          </Form.Footer>
        </Form>
      </FormProvider>
    </>
  )
}

export default PostalCodeStep
